// @ts-nocheck
// Verbatim port of design/Login.html. Only changes from the design file:
//   1. One centered card with two panes (reassurance left, form right) on
//      desktop; the form pane alone, edge to edge, on phones. Inter type
//      scale; the old scoped <style> (Instrument Serif / IBM Plex Sans) is
//      gone. Disabled buttons show a one-line reason.
//   2. The "Email me a sign-in code" button now calls /v1/auth/sign-in.
//   3. The "Verify & log in" button calls /v1/auth/verify-code and stores
//      the resulting JWT via useAuth().
import React, { useState, useEffect, useRef } from "react";
import * as LucideIcons from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { auth as authApi, applications, API_BASE_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Wordmark } from "../components/Wordmark";
import { Spark } from "../components/Spark";
import { peekReferralCode, consumeReferralCode } from "./ReferralLanding";
import { consumeNextLocation, peekNextLocation } from "../lib/nextLocation";
import { PLANS, planFrom, SIGNUP_NAME_KEY, FEE_CALCULATOR_URL } from "../lib/plans";
import { TERMS_URL, PRIVACY_URL } from "../lib/legal";
import { readScanResult } from "../lib/scanResult";
import { saveScan } from "../lib/caseProgress";

// Set once a sign-in has succeeded in this browser. Until then the page
// greets first-time visitors ("Sign in or create your account") instead of
// "Welcome back." — the marketing CTA lands brand-new users here.
const HAS_SIGNED_IN_KEY = "scholarpetition.hasSignedIn";
function readHasSignedIn() {
  try { return localStorage.getItem(HAS_SIGNED_IN_KEY) === "1"; } catch { return false; }
}
function markHasSignedIn() {
  try { localStorage.setItem(HAS_SIGNED_IN_KEY, "1"); } catch {}
}

function I({ name, cls = "w-4 h-4" }) {
  const pascal = name.split("-").map(s => s[0].toUpperCase() + s.slice(1)).join("");
  const Icon = LucideIcons[pascal];
  return Icon ? <Icon className={cls} /> : null;
}

// Derive a sensible default workspace name from the email so first-time
// signups can hit /sign-in without filling out a separate workspace field.
function workspaceFromEmail(email) {
  const local = (email.split("@")[0] || "").replace(/[+.].*$/, "");
  const human = local.replace(/[._-]+/g, " ").trim();
  return human ? `${human}'s petition` : "My petition";
}

export default function SignIn() {
  const navigate = useNavigate();
  // Arrived from the website with a Scholar link: say it's kept, rather
  // than asking them to paste it again.
  const [scholarSaved] = useState(() => {
    try { return /[?&]scholar=/.test(sessionStorage.getItem("vg-next") || ""); } catch (e) { return false; }
  });
  const location = useLocation();
  // Arrived from a price button on the website (/welcome?plan=eb1a): this is
  // a sign-up for that plan, so ask for their name and say what comes next.
  const [plan] = useState(() => planFrom((location.state as { next?: string } | null)?.next || peekNextLocation()));
  const [name, setName] = useState("");
  const { signIn } = useAuth();
  const [returning] = useState(readHasSignedIn);
  // Where ProtectedRoute wanted the user to go (pathname + search, e.g.
  // "/scholar-path?scholar=…"). Router state wins; sessionStorage is the
  // fallback that survives the Google popup round-trip and a refresh.
  const resolveNext = () => {
    const fromState = (location.state as { next?: string } | null)?.next;
    const stored = consumeNextLocation();
    const next = fromState && fromState.startsWith("/") && !fromState.startsWith("//") ? fromState : stored;
    return next && next !== "/sign-in" ? next : "/scholar-path";
  };
  // Shared post-auth landing: mark the browser as having signed in, pick
  // the right conv-step for new vs returning accounts, then navigate.
  const finishSignIn = async () => {
    markHasSignedIn();
    try {
      localStorage.setItem("conv-step", "1");
    } catch {}
    try {
      const apps = await applications.list();
      let list = Array.isArray(apps) ? apps : (apps as { items?: unknown[] })?.items;
      // An assessment taken before sign-up is only in this browser until a
      // case exists. Create the case now and save it, so signing in from
      // another browser later doesn't send the person back to the quiz.
      const localScan = readScanResult();
      if (Array.isArray(list) && list.length === 0 && localScan) {
        await applications.create({ application_type: "eb1a", title: "EB-1A petition" });
        await saveScan(localScan, localStorage.getItem("sp-scholar-url") || undefined);
        list = [localScan];
      }
      if (Array.isArray(list) && list.length > 0) {
        const forkSeen = (() => {
          try { return localStorage.getItem("attorney-fork-seen") === "1"; } catch { return false; }
        })();
        localStorage.setItem("conv-step", forkSeen ? "6" : "5");
      }
    } catch {
      // Best-effort. conv-step stays at 1 (set above) — user lands at
      // the Scholar URL prompt, which is the right default for a new
      // account anyway.
    }
    navigate(resolveNext());
  };
  const [step, setStep] = useState("email"); // email | code
  const [email, setEmail] = useState("");
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  // "Continue with Google" state. googleBusy hides re-clicks while the
  // popup is open; googleAvailable is set on mount by a /sign-in-url
  // probe — if the backend returns 503 oauth_not_configured (Google
  // creds aren't set on the deployment), the button is hidden entirely
  // rather than throwing on first click.
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleAvailable, setGoogleAvailable] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/v1/auth/google/sign-in-url`);
        if (!cancelled) setGoogleAvailable(res.ok);
      } catch {
        if (!cancelled) setGoogleAvailable(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // "Signed out" confirmation banner — shown briefly after auth.signOut() so
  // the user can see the action took effect. Reads a short-lived marker
  // sessionStorage key written by signOut, then clears it so a refresh
  // doesn't re-show the banner.
  const [showSignedOut, setShowSignedOut] = useState(() => {
    try {
      const t = Number(sessionStorage.getItem("scholarpetition.signedOutAt") || 0);
      const recent = t > 0 && Date.now() - t < 10000;
      if (recent) sessionStorage.removeItem("scholarpetition.signedOutAt");
      return recent;
    } catch {
      return false;
    }
  });
  useEffect(() => {
    if (!showSignedOut) return;
    const id = window.setTimeout(() => setShowSignedOut(false), 5000);
    return () => window.clearTimeout(id);
  }, [showSignedOut]);
  const [error, setError] = useState("");
  const nameOk = !plan || name.trim().length >= 2;
  const valid = /\S+@\S+\.\S+/.test(email) && nameOk;
  const codeRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];
  const codeFull = code.every(d => d !== "");

  const setDigit = (i, v) => {
    const d = v.replace(/\D/g, "").slice(-1);
    setCode(c => { const n = [...c]; n[i] = d; return n; });
    if (d && i < 5) codeRefs[i + 1].current && codeRefs[i + 1].current.focus();
  };
  const onCodeKey = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) codeRefs[i - 1].current && codeRefs[i - 1].current.focus();
  };
  const onCodePaste = (i, e) => {
    const text = (e.clipboardData || window.clipboardData)?.getData("text") || "";
    const digits = text.replace(/\D/g, "").slice(0, 6 - i);
    if (!digits) return;
    e.preventDefault();
    setCode(c => {
      const n = [...c];
      for (let k = 0; k < digits.length && i + k < 6; k++) n[i + k] = digits[k];
      return n;
    });
    const last = Math.min(i + digits.length, 5);
    codeRefs[last].current && codeRefs[last].current.focus();
  };

  const continueWithGoogle = async () => {
    if (googleBusy || !googleAvailable) return;
    setError("");
    setGoogleBusy(true);
    let popup: Window | null = null;
    try {
      // Fetch the auth URL fresh per click — the state token embedded
      // in it is short-lived (10 min) and single-use.
      const res = await fetch(`${API_BASE_URL}/v1/auth/google/sign-in-url`);
      if (!res.ok) throw new Error("Google sign-in is not configured.");
      const { auth_url } = await res.json();
      // Center a small popup. Pop-up blockers usually allow this since
      // it's the direct result of a user click.
      const w = 480, h = 640;
      const left = window.screenX + Math.max(0, (window.outerWidth - w) / 2);
      const top = window.screenY + Math.max(0, (window.outerHeight - h) / 2);
      popup = window.open(
        auth_url,
        "visagenius-google-signin",
        `width=${w},height=${h},left=${left},top=${top},menubar=no,toolbar=no,location=no`,
      );
      if (!popup) {
        throw new Error("Pop-up blocked. Allow pop-ups for this site and try again.");
      }
      // Listen for the postMessage from /v1/auth/google/sign-in-callback.
      // Match origin (same as window.location.origin — the backend sets
      // targetOrigin to opener.location.origin) and the source tag.
      const onMessage = (e: MessageEvent) => {
        if (e.origin !== window.location.origin) return;
        const data = e.data;
        if (!data || data.source !== "visagenius:sign-in") return;
        window.removeEventListener("message", onMessage);
        window.clearInterval(closedPoller);
        setGoogleBusy(false);
        if (!data.ok) {
          setError(
            data.reason === "email_not_verified"
              ? "Your Google account's email isn't verified yet."
              : data.reason === "account_disabled"
                ? "This account is disabled. Contact your workspace admin."
                : "Google sign-in failed. Try again or use email below."
          );
          return;
        }
        if (data.token) {
          signIn(data.token);
          finishSignIn();
        }
      };
      window.addEventListener("message", onMessage);
      // Belt-and-braces: if the user closes the popup without
      // completing, clear the busy state so they can retry.
      const closedPoller = window.setInterval(() => {
        if (popup && popup.closed) {
          window.clearInterval(closedPoller);
          window.removeEventListener("message", onMessage);
          setGoogleBusy(false);
        }
      }, 500);
    } catch (e: any) {
      try { popup?.close(); } catch {}
      setGoogleBusy(false);
      setError(e?.message || "Google sign-in failed. Please try again.");
    }
  };

  const requestCode = async () => {
    if (!valid || sending) return;
    setError("");
    setSending(true);
    try {
      // Forward a captured referral slug (set when the user landed
      // via /r/{slug}) so the backend credits the referrer on signup.
      // peek here — the actual consume happens on verifyCode so a
      // failed code entry doesn't lose the attribution.
      const referral_code = peekReferralCode();
      const fullName = plan ? name.trim().replace(/\s+/g, " ") : "";
      try { if (fullName) sessionStorage.setItem(SIGNUP_NAME_KEY, fullName); } catch {}
      await authApi.signIn({
        email: email.toLowerCase().trim(),
        workspace_name: fullName || workspaceFromEmail(email),
        ...(referral_code ? { referral_code } : {}),
      });
      setStep("code");
    } catch (e) {
      setError(e?.message || "Could not send code. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const verifyCode = async () => {
    if (!codeFull || verifying) return;
    setError("");
    setVerifying(true);
    try {
      // Consume (read + clear) the captured referral slug now that the
      // user is actually completing the signup. The backend credits the
      // referrer in _apply_referral_attribution; consuming here means
      // the code can't leak into a later, unrelated sign-in.
      const referral_code = consumeReferralCode();
      const tok = await authApi.verifyCode({
        email: email.toLowerCase().trim(),
        code: code.join(""),
        ...(referral_code ? { referral_code } : {}),
      });
      signIn(tok);
      // Default new users to step 1 (EligibilityScholar — Google Scholar URL
      // prompt). Returning users with saved work jump ahead: to step 5
      // (AttorneyFirst — Talk-to-attorney vs Start-building fork) on the
      // first sign-in, then straight to step 6 (CasePlan) once they've
      // already chosen a path through the fork (attorney-fork-seen flag).
      // If ProtectedRoute saved an intended URL, land there instead.
      await finishSignIn();
    } catch (e) {
      setError(e?.message || "Invalid or expired code.");
    } finally {
      setVerifying(false);
    }
  };

  // Auto-submit once the user fills the 6th digit.
  useEffect(() => {
    if (step === "code" && codeFull && !verifying) verifyCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFull, step]);

  // One-line reason under the disabled primary button (SYSTEM-v2).
  const emailReason = !nameOk ? "Enter your full name to continue" : !email.trim() ? "Enter your email to continue" : !valid ? "Check your email address — it looks incomplete" : "";
  const codeReason = !codeFull ? "Enter all 6 digits — we'll log you in automatically" : "";

  return (
    <div className="signin-root min-h-screen bg-soft text-ink flex items-center justify-center sm:p-[18px]">
      {/* One centered card with two panes: reassurance on the left, the
          form on the right. Phones get the form pane alone, edge to edge. */}
      <div className="w-full sm:w-[min(960px,98vw)] min-h-screen sm:min-h-[620px] grid md:grid-cols-[0.92fr_1.08fr] bg-surface sm:rounded-[22px] overflow-hidden sm:border sm:border-border" style={{ boxShadow: "0 1px 2px rgba(31,29,24,0.04), 0 40px 90px -48px rgba(31,29,24,0.45)" }}>
      {/* left — reassurance / brand */}
      <aside className="hidden md:flex flex-col px-9 py-9 bg-sage-50 border-r border-sage-100">
        <a href="/scholar-path" className="inline-flex items-center gap-2.5 focus-ring rounded-lg self-start" style={{ textDecoration: "none" }}>
          <Spark size={36} tile="forest" />
          <Wordmark size={20} />
        </a>
        <div className="my-auto">
          {plan ? (
            <>
              <h2 className="font-display text-h2 text-ink">{PLANS[plan].waitlist ? `${PLANS[plan].name}, coming soon.` : `You chose ${PLANS[plan].name}.`}</h2>
              <p className="mt-3 text-body text-ink-2"><b className="text-ink">{PLANS[plan].price}</b>{!PLANS[plan].waitlist && <> · <a href={FEE_CALCULATOR_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">USCIS filing fees</a> not included</>}. {PLANS[plan].terms}</p>
            </>
          ) : returning ? (
            <>
              <h2 className="font-display text-h2 text-ink">Your case is right where you left it.</h2>
              <p className="mt-3 text-body text-ink-2">Pick up your EB-1A petition, message your case team, and track your filing — all in one place.</p>
            </>
          ) : (
            <>
              <h2 className="font-display text-h2 text-ink">{scholarSaved ? "Your Scholar link is saved." : "Start your free eligibility read in two minutes."}</h2>
              <p className="mt-3 text-body text-ink-2">{scholarSaved
                ? "Enter your email to save your results. Your free read starts as soon as you're in. Add your CV and see how your record lines up against the eight EB-1A criteria."
                : "Paste your Google Scholar link, add your CV, and see how your record lines up against the eight EB-1A criteria."}</p>
            </>
          )}
          <ul className="mt-6 space-y-3">
            {[["save", "Progress saves at every step"], ["lock", "Encrypted in transit and at rest"], ["message-circle", "Your case team, one message away"]].map(([ic, t]) => (
              <li key={t} className="flex items-center gap-2.5 text-small text-ink-1">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0 bg-surface text-sage-700 border border-sage-100"><I name={ic} cls="w-3.5 h-3.5" /></span>
                {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-8 pt-5 text-small text-ink-2 border-t border-sage-100">
          New here? Just enter your email — no password needed.
        </div>
      </aside>

      {/* right — login form */}
      <main className="flex flex-col">
        {/* phone/tablet brand bar */}
        <div className="md:hidden h-14 flex items-center px-4 sm:px-6 border-b border-line bg-surface">
          <a href="/scholar-path" className="inline-flex items-center gap-2 focus-ring rounded-lg" style={{ textDecoration: "none" }}>
            <Spark size={28} tile="forest" />
            <Wordmark size={17} />
          </a>
        </div>
        <div className="flex-1 flex items-start sm:items-center justify-center px-5 py-8 sm:px-10 sm:py-9">
          <div className="w-full max-w-[360px]">
            {step === "code" ? (
              <div className="fade">
                <span className="w-12 h-12 rounded-2xl flex items-center justify-center text-white mb-5 bg-sage-500"><I name="mail-check" cls="w-6 h-6" /></span>
                <h1 className="font-display text-h1 text-ink">Enter your code</h1>
                <p className="mt-2 text-body text-ink-2">We sent a 6-digit code to <b className="text-ink-1 break-all">{email}</b>. It expires in 30 minutes.</p>
                <div className="grid grid-cols-6 gap-2 mt-6" role="group" aria-label="6-digit code">
                  {code.map((d, i) => (
                    <input key={i} ref={codeRefs[i]} aria-label={`Digit ${i + 1}`} className="g-input text-center tnum !px-0 h-14 text-h2 font-semibold" inputMode="numeric" autoComplete={i === 0 ? "one-time-code" : "off"} maxLength={1} value={d} onChange={e => setDigit(i, e.target.value)} onKeyDown={e => onCodeKey(i, e)} onPaste={e => onCodePaste(i, e)} autoFocus={i === 0} />
                  ))}
                </div>
                {error && <div role="alert" className="vg-notice mt-3 text-small text-terra-700">{error}</div>}
                <button className="btn-primary focus-ring w-full h-12 text-body mt-6" disabled={!codeFull || verifying} onClick={verifyCode}>
                  {verifying ? "Verifying…" : <>Verify &amp; log in <I name="arrow-right" cls="w-4 h-4" /></>}
                </button>
                {codeReason && !verifying && <p className="text-small text-ink-2 mt-2.5 text-center">{codeReason}</p>}
                <div className="flex items-center justify-between gap-3 mt-5 text-small">
                  <button className="text-ink-2 hover:text-ink focus-ring rounded inline-flex items-center gap-1.5 h-10" onClick={() => { setStep("email"); setCode(["", "", "", "", "", ""]); setError(""); }}><I name="arrow-left" cls="w-4 h-4" /> Change email</button>
                  <button className="font-semibold text-terra-700 hover:underline focus-ring rounded h-10" onClick={requestCode} disabled={sending}>{sending ? "Sending…" : "Resend code"}</button>
                </div>
              </div>
            ) : (
              <div className="fade">
                {showSignedOut && (
                  <div role="status" className="mb-5 rounded-xl px-3.5 py-2.5 flex items-center gap-2 vg-notice bg-sage-50 border border-sage-100 text-sage-700">
                    <I name="check-circle-2" cls="w-4 h-4 shrink-0" />
                    <span className="text-small">Signed out. Your case is safely saved.</span>
                  </div>
                )}
                {plan ? (
                  <>
                    <h1 className="font-display text-h1 text-ink">{PLANS[plan].waitlist ? "Join the waitlist" : `Start with ${PLANS[plan].name}`}</h1>
                    <p className="mt-2 text-body text-ink-2">{PLANS[plan].waitlist
                      ? "Add your name and email, and we'll let you know when attorney review opens."
                      : PLANS[plan].founder
                        ? "Add your name and email to create your account. Next, you'll start your founder plan."
                        : `${PLANS[plan].price}. Add your name and email to create your account. Next, you'll book a free kickoff call.`}</p>
                  </>
                ) : returning ? (
                  <>
                    <h1 className="font-display text-h1 text-ink">Welcome back</h1>
                    <p className="mt-2 text-body text-ink-2">Log in to continue your EB-1A petition.</p>
                  </>
                ) : (
                  <>
                    <h1 className="font-display text-h1 text-ink">Sign in or create your account</h1>
                    <p className="mt-2 text-body text-ink-2">Enter your email — if you're new, we'll create your account.</p>
                  </>
                )}

                {/* Google sign-in is hidden entirely (not just disabled) when
                    the deployment has no Google credentials — the probe on
                    mount returns 503 oauth_not_configured. */}
                {googleAvailable && (
                  <>
                    <button
                      className="btn-secondary focus-ring w-full h-12 text-body mt-7"
                      onClick={continueWithGoogle}
                      disabled={googleBusy}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
                      {googleBusy ? "Continuing…" : "Continue with Google"}
                    </button>
                    <div className="flex items-center gap-3 my-6"><span className="h-px flex-1 bg-hair"></span><span className="text-small text-ink-3">or use your email</span><span className="h-px flex-1 bg-hair"></span></div>
                  </>
                )}

                {plan && (
                  <>
                    <label htmlFor="signin-name" className={`block text-small font-semibold text-ink-1 ${googleAvailable ? "" : "mt-7"}`}>Full name</label>
                    <input id="signin-name" className="g-input mt-1.5" autoComplete="name" placeholder="Your full name" value={name} onChange={e => setName(e.target.value)} />
                  </>
                )}
                <label htmlFor="signin-email" className={`block text-small font-semibold text-ink-1 ${plan ? "mt-4" : googleAvailable ? "" : "mt-7"}`}>Email</label>
                <input id="signin-email" className="g-input mt-1.5" type="email" autoComplete="email" placeholder="you@university.edu" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && valid) requestCode(); }} aria-describedby="signin-email-hint" />
                {error && <div role="alert" className="vg-notice mt-3 text-small text-terra-700">{error}</div>}
                <button className="btn-primary focus-ring w-full h-12 text-body mt-4" disabled={!valid || sending} onClick={requestCode}>
                  <I name="mail" cls="w-4 h-4" /> {sending ? "Sending…" : "Email me a sign-in code"}
                </button>
                <p id="signin-email-hint" className="text-small mt-2.5 text-center" aria-live="polite">
                  {emailReason
                    ? <span className="text-ink-2">{emailReason}</span>
                    : <span className="text-ink-3">No password needed — we'll email you a 6-digit code.</span>}
                </p>

                <p className="text-small text-ink-3 mt-7 pt-5 text-center border-t border-hair">By continuing you agree to our <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-ink-2 focus-ring rounded">Terms</a> and <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-ink-2 focus-ring rounded">Privacy Policy</a>.</p>
              </div>
            )}
          </div>
        </div>
      </main>
      </div>
    </div>
  );
}
